import { actionDraw } from "../deck/actionDraw.js";
import { accept, reject } from "../reducer/reject.js";
import type { CardInstanceId, Command, GameState, RunState } from "../types/index.js";
import { continuePendingRepeat, withReshuffleLog } from "./completion.js";

type CardChoice = Extract<NonNullable<RunState["pendingChoice"]>, {
  kind: "cellar" | "knowledgeShare" | "caseExclude";
}>;

function unreachableChoice(choice: never): never {
  throw new Error(`Unsupported card choice: ${String(choice)}`);
}

function toggleInList(list: CardInstanceId[], id: CardInstanceId): CardInstanceId[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function resolveCellar(run: RunState, selected: CardInstanceId[]): { run: RunState; reshuffled: boolean } {
  const chosen = run.hand.filter((card) => selected.includes(card.instanceId));
  const hand = run.hand.filter((card) => !selected.includes(card.instanceId));
  const drawn = actionDraw({ ...run, discard: [...run.discard, ...chosen] }, chosen.length);
  return {
    reshuffled: drawn.reshuffled,
    run: {
      ...drawn.run,
      hand: [...hand, ...drawn.drawn],
      deck: drawn.deck,
      discard: drawn.discard,
      rngState: drawn.rngState,
      pendingChoice: null,
    },
  };
}

function resolveKnowledgeShare(run: RunState, selected: CardInstanceId[]): RunState {
  if (selected.length === 0 || run.discard.length === 0) return { ...run, pendingChoice: null };
  const card = run.discard.find((item) => item.instanceId === selected[0]);
  if (!card) return { ...run, pendingChoice: null };
  return {
    ...run,
    discard: run.discard.filter((item) => item.instanceId !== card.instanceId),
    deck: [...run.deck, card],
    pendingChoice: null,
  };
}

export function toggleCardChoice(
  state: GameState,
  pending: CardChoice,
  command: Extract<Command, { type: "TOGGLE_SELECTION" }>,
): GameState {
  const id = command.instanceId;
  if (!id) return reject(state, "この選択ではトグルできない");

  if (pending.kind === "cellar" || pending.kind === "knowledgeShare" || pending.kind === "caseExclude") {
    const targets = pending.kind === "knowledgeShare" ? state.run!.discard : state.run!.hand;
    if (!targets.some((card) => card.instanceId === id)) return reject(state, "選択対象外のカード");
    const selected = pending.kind !== "cellar" && pending.selected.length >= 1
      ? pending.selected[0] === id ? [] : [id]
      : toggleInList(pending.selected, id);
    return accept({ ...state, run: { ...state.run!, pendingChoice: { ...pending, selected } } });
  }

  return reject(state, "この選択ではトグルできない");
}

export function submitCardChoice(state: GameState, pending: CardChoice): GameState {
  const run = state.run!;
  if (pending.kind === "caseExclude") {
    const selected = run.hand.filter((card) => pending.selected[0] === card.instanceId);
    return accept({ ...state, run: continuePendingRepeat({
      ...run,
      hand: run.hand.filter((card) => !selected.includes(card)),
      caseExcluded: [...(run.caseExcluded ?? []), ...selected],
      pendingChoice: null,
    }) });
  }
  if (pending.kind === "cellar") {
    const result = resolveCellar(run, pending.selected);
    return accept({ ...state, run: withReshuffleLog(run, continuePendingRepeat(result.run), result.reshuffled) });
  }
  if (pending.kind === "knowledgeShare") {
    return accept({ ...state, run: continuePendingRepeat(resolveKnowledgeShare(run, pending.selected)) });
  }

  return unreachableChoice(pending);

}
